import {
  CollapsibleTagList,
  BookmarkButton,
  ExpandableContent,
  ExternalLink,
  Link,
} from '@/components/Elements';
import { TopicTagWithTooltip } from '@/features/analyze/components/TopicTagWithTooltip';
import {
  MetadataTag,
  TagContainer,
  ThemeTag,
} from '@/features/analyze/components/Tag';
import { getSafeExternalUrl } from '@/utils/url';
import { useSearchResultTagFilter } from '@/features/analyze/hooks/useSearchResultTagFilter';
import { FragmentOf, graphql, readFragment } from '@/lib/graphql';
import { format } from '@formkit/tempo';
import {
  BuildingOffice2Icon,
  CubeIcon,
  GlobeAltIcon,
  HashtagIcon,
  BookOpenIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { type FacetKey } from '@/features/analyze/types';
import { useTranslation } from 'react-i18next';

const searchResultItemFragment = graphql(`
  fragment SearchResultItemFragment on ActiveArticle {
    code
    title
    url
    summary
    publishedAt
    country
    organizationType
    organization
    displayableTopicTags {
      lookupKey
      name
      description
      themes
      isSocialIssue
    }
    themes
    imageUrl
    isBookmarkedByCurrentUser
  }
`);

export type SearchResultProps = {
  docs: ReadonlyArray<FragmentOf<typeof searchResultItemFragment>>;
};

export const SearchResult = ({ docs }: SearchResultProps) => {
  return (
    <div className=" flex w-full flex-col gap-y-6">
      {docs.map((doc) => (
        <SearchResultItem
          key={readFragment(searchResultItemFragment, doc).code}
          data={doc}
        />
      ))}
    </div>
  );
};

SearchResult.fragments = {
  data: searchResultItemFragment,
};

const SearchResultItem = ({
  data,
}: {
  data: FragmentOf<typeof searchResultItemFragment>;
}) => {
  const item = readFragment(searchResultItemFragment, data);
  const addSearchResultTagFilter = useSearchResultTagFilter();
  const safeItemUrl = getSafeExternalUrl(item.url);
  const { t } = useTranslation();
  const detailPath = `/articles/${item.code}?from=search`;
  const iconClassName =
    'grid size-7 place-items-center rounded-full bg-rw-white transition-colors hover:bg-rw-white/80';
  const hasTagGroup =
    (item.themes?.length ?? 0) > 0 ||
    Boolean(item.country) ||
    Boolean(item.organizationType) ||
    Boolean(item.organization) ||
    (item.displayableTopicTags?.length ?? 0) > 0;

  const onClick = (targetFieldKey: FacetKey, clickText: string | null) => {
    if (clickText == null) return;
    addSearchResultTagFilter(targetFieldKey, clickText);
  };

  return (
    <div className="flex gap-x-6">
      <img
        src={item.imageUrl || '/images/article-no-image.jpg'}
        alt="Article Thumbnail"
        className="hidden size-40 min-h-40 min-w-40 rounded-tr-md object-cover lg:block"
        onError={(e) => (e.currentTarget.src = '/images/article-no-image.jpg')}
      />
      <div className="flex flex-1 flex-col gap-y-2 overflow-visible">
        <div className="flex items-start justify-between">
          {safeItemUrl ? (
            <ExternalLink
              to={safeItemUrl}
              underline="hover"
              className="grow text-lg font-semibold leading-tight md:text-xl"
            >
              {item.title}
            </ExternalLink>
          ) : (
            <Link
              to={detailPath}
              underline="hover"
              className="grow text-lg font-semibold leading-tight md:text-xl"
            >
              {item.title}
            </Link>
          )}
          <div className="ml-2 flex shrink-0 items-center gap-2">
            <Link
              to={detailPath}
              className={iconClassName}
              aria-label={t('記事詳細を開く')}
              title={t('記事詳細を開く')}
            >
              <BookOpenIcon className="size-4 stroke-2 text-rw-sub" />
            </Link>
            <BookmarkButton
              code={item.code}
              title={item.title}
              url={item.url}
              isBookmarked={item.isBookmarkedByCurrentUser}
            />
          </div>
        </div>
        {hasTagGroup && (
          <div className="rounded-lg">
            <TagContainer>
              {item.themes && item.themes.length > 0 && (
                <CollapsibleTagList
                  items={item.themes}
                  maxVisibleItems={3}
                  textColor="text-rw-sub"
                  renderTag={(theme) => (
                    <ThemeTag
                      key={theme}
                      icon={HashtagIcon}
                      tableName={theme}
                      onClick={() => onClick('themes', theme)}
                    />
                  )}
                />
              )}

              {/* メタデータタグ（国・組織タイプ・組織名） */}
              {item.country && (
                <MetadataTag
                  icon={GlobeAltIcon}
                  text={item.country}
                  onClick={() => onClick('countries', item.country)}
                />
              )}
              {item.organizationType && (
                <MetadataTag
                  icon={CubeIcon}
                  text={item.organizationType}
                  onClick={() =>
                    onClick('organizationTypes', item.organizationType)
                  }
                />
              )}
              {item.organization && (
                <MetadataTag
                  icon={BuildingOffice2Icon}
                  text={item.organization}
                  onClick={() => onClick('organizations', item.organization)}
                />
              )}
              {item.displayableTopicTags &&
                item.displayableTopicTags.length > 0 && (
                  <CollapsibleTagList
                    items={item.displayableTopicTags}
                    maxVisibleItems={10}
                    textColor="text-rw-black"
                    renderTag={(tag) => (
                      <TopicTagWithTooltip
                        key={tag.lookupKey}
                        name={tag.name}
                        description={tag.description}
                        themes={tag.themes}
                        isSocialIssue={tag.isSocialIssue}
                        icon={TagIcon}
                        onClick={() => onClick('topicTags', tag.lookupKey)}
                      />
                    )}
                  />
                )}
            </TagContainer>
          </div>
        )}
        <ExpandableContent aria-label="記事サマリ" className="line-clamp-3">
          {`${format(item.publishedAt, { date: 'short' })} - ${item.summary ?? ''}`}
        </ExpandableContent>
      </div>
    </div>
  );
};
